/* assets/manual-guide-composite.js */

(() => {
  const ROOT_SEL = "[data-mg-root]";

  // Accordion
  const ACC_HEADER_SEL = ".mg__header";
  const ACC_TOGGLE_SEL = "[data-mg-acc-toggle]";
  const ACC_CONTENT_SEL = "[data-mg-acc-content]";
  const ACC_HEADING_SEL = ".mg__heading"; // h2 click toggle (mobile)

  // Video
  const CARD_SEL = "[data-mg-video-card]";
  const LIST_SEL = "[data-mg-video-list]";
  const ITEM_SEL = "[data-mg-video-item]";
  const PLAYER_SEL = "[data-mg-player]";

  const EMBED_HOSTS = ["www.youtube.com", "youtube.com", "www.youtube-nocookie.com", "youtube-nocookie.com"];

  const PLACEHOLDER_STRS = new Set(["none", "n/a", "na"]);
  const PLACEHOLDER_ID = "dqw4w9wgxcq";

  const STATE = new WeakMap();

  // Font scale (section-1 style)
  // 1800px=1.0, 900~1800=0.85~1.0, 1800~3000=1.0~1.25, clamp 0.85~1.25
  const ROOTS = new Set();
  let rafScale = 0;

  // Breakpoint (MATCH SECTION A)
  const MQ_DESKTOP = window.matchMedia ? window.matchMedia("(min-width: 990px)") : null;
  const isDesktop = () => (MQ_DESKTOP ? MQ_DESKTOP.matches : window.innerWidth >= 990);

  function clamp(n, a, b) {
    return Math.max(a, Math.min(b, n));
  }

  function computeScale(vw) {
    const w = Math.max(0, vw || 0);

    if (w <= 900) return 0.85;
    if (w < 1800) {
      const t = (w - 900) / 900;
      return 0.85 + t * (1.0 - 0.85);
    }
    if (w < 3000) {
      const t = (w - 1800) / 1200;
      return 1.0 + t * (1.25 - 1.0);
    }
    return 1.25;
  }

  function applyScale(root) {
    if (!root) return;
    const s = clamp(computeScale(window.innerWidth), 0.85, 1.25);
    root.style.setProperty("--mg-scale", String(Math.round(s * 1000) / 1000));
  }

  function safeUrl(str) {
    try {
      return new URL(str);
    } catch (e) {
      return null;
    }
  }

  function isYoutubeEmbedUrl(urlStr) {
    const u = safeUrl(urlStr);
    if (!u) return false;
    const host = (u.hostname || "").toLowerCase();
    if (!EMBED_HOSTS.some((h) => host === h)) return false;
    const path = u.pathname || "";
    return path.startsWith("/embed/");
  }

  function withYoutubeEmbedParams(embedSrc) {
    if (!isYoutubeEmbedUrl(embedSrc)) return embedSrc;
    const u = safeUrl(embedSrc);
    if (!u) return embedSrc;

    if (!u.searchParams.has("rel")) u.searchParams.set("rel", "0");
    if (!u.searchParams.has("modestbranding")) u.searchParams.set("modestbranding", "1");
    if (!u.searchParams.has("playsinline")) u.searchParams.set("playsinline", "1");

    return u.toString();
  }

  function isPlaceholderRaw(raw) {
    const s = (raw || "").trim();
    if (!s) return true;
    const l = s.toLowerCase();
    if (PLACEHOLDER_STRS.has(l)) return true;
    if (l.includes(PLACEHOLDER_ID)) return true;
    return false;
  }

  function getYoutubeIdFromUrl(url) {
    const u = safeUrl(url);
    if (!u) return "";

    const host = (u.hostname || "").toLowerCase();
    const path = u.pathname || "";

    // youtu.be/<id>
    if (host.includes("youtu.be")) {
      return path.replace("/", "").trim().split(/[?&/]/)[0] || "";
    }

    // youtube.com
    if (host.includes("youtube.com") || host.includes("youtube-nocookie.com")) {
      if (path === "/watch") return (u.searchParams.get("v") || "").trim();
      if (path.startsWith("/shorts/")) return (path.split("/shorts/")[1] || "").split(/[?&/]/)[0] || "";
      if (path.startsWith("/embed/")) return (path.split("/embed/")[1] || "").split(/[?&/]/)[0] || "";
    }

    return "";
  }

  function getYoutubeIdFromEmbedSrc(src) {
    const u = safeUrl(src);
    if (!u) return "";

    const host = (u.hostname || "").toLowerCase();
    if (!EMBED_HOSTS.some((h) => host === h)) return "";

    const path = u.pathname || "";
    if (!path.startsWith("/embed/")) return "";

    return (path.split("/embed/")[1] || "").split(/[?&/]/)[0] || "";
  }

  function toEmbedSrc(idOrUrl) {
    const raw = (idOrUrl || "").trim();
    if (!raw) return "";

    // already a youtube embed URL
    if (raw.includes("youtube.com/embed/") || raw.includes("youtube-nocookie.com/embed/")) {
      return withYoutubeEmbedParams(raw);
    }

    // URL -> youtube id, else keep as-is (non-youtube)
    if (raw.includes("http")) {
      const id = getYoutubeIdFromUrl(raw);
      if (id) return withYoutubeEmbedParams(`https://www.youtube.com/embed/${id}`);
      return raw; // non-youtube URL fallback
    }

    // assume youtube id
    return withYoutubeEmbedParams(`https://www.youtube.com/embed/${raw}`);
  }

  function srcFromRaw(raw) {
    const s = (raw || "").trim();
    if (!s) return "";
    if (isPlaceholderRaw(s)) return "";

    // iframe pasted: extract src
    if (s.includes("<iframe")) {
      const m1 = s.match(/src="([^"]+)"/i);
      if (m1 && m1[1]) return isYoutubeEmbedUrl(m1[1]) ? withYoutubeEmbedParams(m1[1]) : m1[1];

      const m2 = s.match(/src='([^']+)'/i);
      if (m2 && m2[1]) return isYoutubeEmbedUrl(m2[1]) ? withYoutubeEmbedParams(m2[1]) : m2[1];

      return "";
    }

    // URL or ID
    return toEmbedSrc(s);
  }

  function setActive(items, el) {
    items.forEach((x) => x.classList.remove("is-active"));
    if (el) el.classList.add("is-active");
  }

  function setPlayerSrc(player, src) {
    if (!player || !src) return;
    const cur = (player.getAttribute("src") || "").trim();
    if (cur !== src) player.setAttribute("src", src);
  }

  function getItemSrc(el) {
    if (!el) return "";
    const type = (el.getAttribute("data-video-type") || "").trim();

    if (type === "video_url") {
      const id = (el.getAttribute("data-video-id") || "").trim();
      if (!id) return "";
      return toEmbedSrc(id);
    }

    const raw = el.getAttribute("data-video-raw") || "";
    return srcFromRaw(raw);
  }

  function findItemByCurrentSrc(items, currentSrc) {
    const curId = getYoutubeIdFromEmbedSrc(currentSrc);
    if (!curId) return null;

    for (const el of items) {
      const src = getItemSrc(el);
      const id = getYoutubeIdFromEmbedSrc(src);
      if (id && id === curId) return el;
    }
    return null;
  }

  function computeState(root) {
    const header = root.querySelector(ACC_HEADER_SEL);
    const toggle = header ? header.querySelector(ACC_TOGGLE_SEL) : null;

    let content = null;
    if (toggle) {
      const id = (toggle.getAttribute("aria-controls") || "").trim();
      if (id) content = document.getElementById(id);
    }
    if (!content) content = root.querySelector(ACC_CONTENT_SEL);

    const accOn =
      header && (header.getAttribute("data-mg-acc-on") || "").trim() === "1" && !!toggle && !!content;

    const card = root.querySelector(CARD_SEL);
    const player = card ? card.querySelector(PLAYER_SEL) : null;
    const list = card ? card.querySelector(LIST_SEL) : null;
    const items = list ? Array.from(list.querySelectorAll(ITEM_SEL)) : [];

    return { root, header, toggle, content, accOn, card, player, list, items };
  }

  function setAccordionExpanded(st, nextExpanded) {
    if (!st || !st.toggle || !st.content) return;

    st.toggle.setAttribute("aria-expanded", nextExpanded ? "true" : "false");

    // content hide marker (mobile CSS target)
    st.content.classList.toggle("is-collapsed", !nextExpanded);

    // section-a style fallback markers (CSS fallback uses both)
    st.root.classList.toggle("is-collapsed", !nextExpanded);
    if (!nextExpanded) st.root.setAttribute("data-mg-collapsed", "1");
    else st.root.removeAttribute("data-mg-collapsed");
  }

  function syncAccordion(st) {
    if (!st || !st.header || !st.toggle || !st.content) return;

    // dropdown disabled: always open
    if (!st.accOn) {
      setAccordionExpanded(st, true);
      return;
    }

    // section-a style: desktop always expanded
    if (isDesktop()) {
      setAccordionExpanded(st, true);
      return;
    }

    const expandedAttr = (st.toggle.getAttribute("aria-expanded") || "").trim();
    const expanded = expandedAttr === "" ? true : expandedAttr !== "false";
    setAccordionExpanded(st, expanded);
  }

  function syncVideo(st) {
    if (!st || !st.player) return;

    const player = st.player;
    const items = st.items || [];
    if (!items.length) return;

    const curSrc = (player.getAttribute("src") || "").trim();
    const matched = curSrc ? findItemByCurrentSrc(items, curSrc) : null;
    if (matched) {
      setActive(items, matched);
      const txt = matched.querySelector(".b3__item-txt");
      const label = txt ? (txt.textContent || "").trim() : "";
      if (label) player.setAttribute("title", label);
      return;
    }

    const initialAttr = (st.root.getAttribute("data-mg-initial-src") || "").trim();
    if (initialAttr) {
      const normalized = srcFromRaw(initialAttr);
      if (normalized) {
        setPlayerSrc(player, normalized);
        const matched2 = findItemByCurrentSrc(items, normalized);
        if (matched2) {
          setActive(items, matched2);
          const txt2 = matched2.querySelector(".b3__item-txt");
          const label2 = txt2 ? (txt2.textContent || "").trim() : "";
          if (label2) player.setAttribute("title", label2);
          return;
        }
      }
    }

    const firstValid = items.find((el) => !!getItemSrc(el));
    if (!firstValid) return;

    const src = getItemSrc(firstValid);
    if (!src) return;

    setPlayerSrc(player, src);
    setActive(items, firstValid);
    const txt = firstValid.querySelector(".b3__item-txt");
    const label = txt ? (txt.textContent || "").trim() : "";
    if (label) player.setAttribute("title", label);
  }

  function ensureBound(root) {
    if (!root || root.getAttribute("data-mg-bound") === "1") return;
    root.setAttribute("data-mg-bound", "1");

    root.addEventListener("click", (e) => {
      const st = computeState(root);
      STATE.set(root, st);

      // toggle icon click
      const tgl = e.target && e.target.closest ? e.target.closest(ACC_TOGGLE_SEL) : null;
      if (tgl && root.contains(tgl)) {
        if (!st.accOn || !st.toggle || !st.content) return;

        const expanded = (st.toggle.getAttribute("aria-expanded") || "true") !== "false";
        setAccordionExpanded(st, !expanded);
        return;
      }

      // mobile: clicking the H2 area toggles (when dropdown enabled)
      const heading = e.target && e.target.closest ? e.target.closest(ACC_HEADING_SEL) : null;
      if (heading && root.contains(heading)) {
        if (isDesktop()) return;
        if (!st.accOn || !st.toggle || !st.content) return;

        // ignore interactive elements inside heading (safety)
        if (e.target && e.target.closest && e.target.closest("a,button,input,textarea,select,label")) return;

        const expanded = (st.toggle.getAttribute("aria-expanded") || "true") !== "false";
        setAccordionExpanded(st, !expanded);
        return;
      }

      // video item click
      const item = e.target && e.target.closest ? e.target.closest(ITEM_SEL) : null;
      if (item && root.contains(item)) {
        if (!st.player) return;

        const src = getItemSrc(item);
        if (!src) return;

        setPlayerSrc(st.player, src);
        setActive(st.items || [], item);

        const txt = item.querySelector(".b3__item-txt");
        const label = txt ? (txt.textContent || "").trim() : "";
        if (label) st.player.setAttribute("title", label);
        return;
      }
    });
  }

  function updateAllScales() {
    ROOTS.forEach((r) => applyScale(r));
  }

  function updateAllAccordions() {
    ROOTS.forEach((r) => {
      const st = computeState(r);
      STATE.set(r, st);
      syncAccordion(st);
    });
  }

  function requestScaleUpdate() {
    if (rafScale) return;
    rafScale = window.requestAnimationFrame(() => {
      rafScale = 0;
      updateAllScales();
      updateAllAccordions();
    });
  }

  window.addEventListener("resize", requestScaleUpdate, { passive: true });
  window.addEventListener("orientationchange", requestScaleUpdate, { passive: true });

  if (MQ_DESKTOP && MQ_DESKTOP.addEventListener) {
    MQ_DESKTOP.addEventListener("change", requestScaleUpdate);
  } else if (MQ_DESKTOP && MQ_DESKTOP.addListener) {
    MQ_DESKTOP.addListener(requestScaleUpdate);
  }

  function initRoot(root) {
    const st = computeState(root);
    STATE.set(root, st);

    ROOTS.add(root);
    applyScale(root);

    ensureBound(root);
    syncAccordion(st);
    syncVideo(st);
  }

  function initAll(scope = document) {
    const roots = Array.from(scope.querySelectorAll(ROOT_SEL));
    roots.forEach(initRoot);
  }

  // initial
  initAll();

  // theme editor: section reload support
  document.addEventListener("shopify:section:load", (e) => {
    if (!e || !e.target) return;
    initAll(e.target);
    requestScaleUpdate();
  });

  document.addEventListener("shopify:section:unload", (e) => {
    if (!e || !e.target) return;
    const roots = Array.from(e.target.querySelectorAll(ROOT_SEL));
    roots.forEach((r) => ROOTS.delete(r));
  });
})();
